import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { TbDownload, TbEye } from 'react-icons/tb';
import Pagination from '../../components/Pagination';
import SessionContext from '../../contexts/SessionContext';
import { formatPrice } from '../../functions';
import {
    downloadCommittedImportRows,
    type CommittedImportExportRow,
} from '../../functions/import/downloadCommittedImportRows';
import {
    fetchAllInventoryImportPreviewRows,
    useInventoryImportPreview,
} from '../../hooks/inventory/useInventoryImportJobs';
import type {
    InventoryImportDraftRow,
    InventoryImportRowFilter,
} from '../../types/inventory';
import { formatInventoryPartTypeLabel, INVENTORY_CATEGORY_OPTIONS } from '../../utils/inventoryConstants';

const PAGE_SIZE = 20;

type HistoryRowTab = Extract<InventoryImportRowFilter, 'imported' | 'failed' | 'skipped'>;

const HISTORY_TABS: Array<{ id: HistoryRowTab; label: string }> = [
    { id: 'imported', label: 'Imported' },
    { id: 'failed', label: 'Failed' },
    { id: 'skipped', label: 'Skipped' },
];

const EMPTY_COPY: Record<HistoryRowTab, string> = {
    imported: 'No imported items.',
    failed: 'No failed items.',
    skipped: 'No skipped items.',
};

/** Fields that have a dedicated column in this modal (matches Import Preview). */
const HISTORY_ISSUE_FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    manufacturerPartNumber: 'Mfg. Part Number',
    additionalPartNumbers: 'Additional Part Numbers',
    category: 'Category',
    partType: 'Part Type',
    supplierName: 'Supplier',
    initialInStockQuantity: 'In-Stock',
    unitCost: 'Avg Unit Cost',
    locationTitle: 'Location',
    unit: 'Unit',
    reorderQuantity: 'Reorder',
    targetQuantity: 'Target',
    restockAlert: 'Restock Alert',
    binNames: 'Bins',
    tagNames: 'Tags',
    imageUrl: 'Image',
};

const HISTORY_ISSUE_FIELDS = new Set(Object.keys(HISTORY_ISSUE_FIELD_LABELS));

function categoryLabel(category: string | null | undefined): string {
    if (!category) return '—';
    return INVENTORY_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;
}

function issueMessageForField(row: InventoryImportDraftRow, field: string): string | null {
    const issue = row.issues.find((i) => i.field === field);
    return issue?.message ?? null;
}

/**
 * Tooltip for the Issue column: row-level messages when present; otherwise a
 * list of column labels that have field-level issues.
 */
function issueColumnTooltip(row: InventoryImportDraftRow): string | null {
    if (row.issues.length === 0) return null;

    const unmatched = row.issues.filter(
        (i) => !i.field || !HISTORY_ISSUE_FIELDS.has(i.field),
    );
    if (unmatched.length > 0) {
        return unmatched.map((i) => i.message).filter(Boolean).join(' · ') || null;
    }

    const labels: string[] = [];
    const seen = new Set<string>();
    for (const issue of row.issues) {
        const field = issue.field!;
        if (seen.has(field)) continue;
        seen.add(field);
        labels.push(HISTORY_ISSUE_FIELD_LABELS[field] ?? field);
    }
    if (labels.length === 0) return null;
    return labels.length === 1
        ? `Issue in ${labels[0]}`
        : `Issues in ${labels.join(', ')}`;
}

function FieldIssueIcon({
    message,
    ml = 1,
}: {
    message: string | null;
    ml?: number;
}) {
    if (!message) return null;
    return (
        <Tooltip label={message} hasArrow>
            <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                ml={ml}
                flexShrink={0}
                alignSelf="center"
            >
                <Alert status="error" variant="subtle" p={0} bg="transparent" w="auto">
                    <AlertIcon boxSize={3.5} m={0} mr={0} />
                </Alert>
            </Box>
        </Tooltip>
    );
}

function ValueWithIssue({
    showIssues,
    field,
    row,
    children,
    isNumeric,
}: {
    showIssues: boolean;
    field: string;
    row: InventoryImportDraftRow;
    children: React.ReactNode;
    isNumeric?: boolean;
}) {
    const message = showIssues ? issueMessageForField(row, field) : null;
    if (!showIssues || !message) {
        return (
            <Td isNumeric={isNumeric}>
                {children}
            </Td>
        );
    }
    return (
        <Td isNumeric={isNumeric}>
            <HStack
                spacing={0}
                align="center"
                justify={isNumeric ? 'flex-end' : 'flex-start'}
                display="inline-flex"
                w="100%"
            >
                <Box as="span">{children}</Box>
                <FieldIssueIcon message={message} />
            </HStack>
        </Td>
    );
}

interface InventoryCommittedImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    jobId: string | null;
}

function useOutcomeTotal(
    jobId: string | null,
    filter: HistoryRowTab,
    enabled: boolean,
): number | undefined {
    const query = useInventoryImportPreview(
        jobId ? { jobId, limit: 1, offset: 0, rowFilter: filter } : null,
        enabled && !!jobId,
    );
    return query.data?.totalItems;
}

export default function InventoryCommittedImportModal({
    isOpen,
    onClose,
    jobId,
}: InventoryCommittedImportModalProps) {
    const toast = useToast();
    const sessionContext = useContext(SessionContext);
    const [page, setPage] = useState(1);
    const [rowFilter, setRowFilter] = useState<HistoryRowTab>('imported');
    const [isDownloading, setIsDownloading] = useState(false);
    const [imageView, setImageView] = useState<{
        url: string;
        name: string;
        mfg: string;
    } | null>(null);
    const imageDisclosure = useDisclosure();

    useEffect(() => {
        setPage(1);
    }, [jobId, rowFilter]);

    const countsEnabled = isOpen && !!jobId;
    const importedTotal = useOutcomeTotal(jobId, 'imported', countsEnabled);
    const failedTotal = useOutcomeTotal(jobId, 'failed', countsEnabled);
    const skippedTotal = useOutcomeTotal(jobId, 'skipped', countsEnabled);

    const previewQuery = useInventoryImportPreview(
        jobId
            ? {
                  jobId,
                  limit: PAGE_SIZE,
                  offset: (page - 1) * PAGE_SIZE,
                  rowFilter,
              }
            : null,
        isOpen && !!jobId,
    );
    const rows = previewQuery.data?.rows ?? [];
    const totalItems = previewQuery.data?.totalItems ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const showIssues = rowFilter === 'failed';

    const tabCounts: Record<HistoryRowTab, number> = {
        imported: rowFilter === 'imported' ? totalItems : (importedTotal ?? 0),
        failed: rowFilter === 'failed' ? totalItems : (failedTotal ?? 0),
        skipped: rowFilter === 'skipped' ? totalItems : (skippedTotal ?? 0),
    };
    const totalOutcomeRows =
        tabCounts.imported + tabCounts.failed + tabCounts.skipped;

    const openImage = (url: string, name: string, mfg: string) => {
        setImageView({ url, name, mfg });
        imageDisclosure.onOpen();
    };

    const closeImage = () => {
        imageDisclosure.onClose();
        setImageView(null);
    };

    const resetAndClose = () => {
        setPage(1);
        setRowFilter('imported');
        setIsDownloading(false);
        closeImage();
        onClose();
    };

    const handleDownload = async () => {
        if (!jobId || !sessionContext?.sessionId || isDownloading) return;
        setIsDownloading(true);
        try {
            const sessionId = sessionContext.sessionId;
            const [imported, failed, skipped] = await Promise.all([
                fetchAllInventoryImportPreviewRows(sessionId, jobId, 'imported'),
                fetchAllInventoryImportPreviewRows(sessionId, jobId, 'failed'),
                fetchAllInventoryImportPreviewRows(sessionId, jobId, 'skipped'),
            ]);
            const exportRows: CommittedImportExportRow[] = [
                ...imported.map((r) => ({ ...r, status: 'Succeeded' as const })),
                ...failed.map((r) => ({ ...r, status: 'Failed' as const })),
                ...skipped.map((r) => ({ ...r, status: 'Skipped' as const })),
            ];
            if (exportRows.length === 0) {
                toast({
                    title: 'Nothing to download',
                    description: 'This import has no rows.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            await downloadCommittedImportRows(
                exportRows,
                `inventory-import-${jobId}.xlsx`,
            );
        } catch (err: unknown) {
            toast({
                title: 'Download failed',
                description: err instanceof Error ? err.message : 'Could not export import rows',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={resetAndClose}
                size="6xl"
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent maxH="90vh">
                    <ModalHeader fontSize="md">
                        Import details
                        {jobId ? (
                            <Text
                                as="span"
                                fontWeight="normal"
                                color="gray.500"
                                ml={2}
                                fontSize="sm"
                            >
                                {jobId}
                            </Text>
                        ) : null}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Flex
                            justify="space-between"
                            align="center"
                            mb={4}
                            gap={2}
                            flexWrap="wrap"
                        >
                            <HStack spacing={1}>
                                {HISTORY_TABS.map((tab) => {
                                    const isActive = rowFilter === tab.id;
                                    return (
                                        <Button
                                            key={tab.id}
                                            size="sm"
                                            h="32px"
                                            px={4}
                                            borderRadius="full"
                                            fontWeight="medium"
                                            fontSize="sm"
                                            bg={isActive ? '#0071E3' : 'gray.100'}
                                            color={isActive ? 'white' : 'gray.700'}
                                            _hover={{
                                                bg: isActive ? '#0071E3' : 'gray.200',
                                            }}
                                            _active={{
                                                bg: isActive ? '#0071E3' : 'gray.200',
                                            }}
                                            onClick={() => setRowFilter(tab.id)}
                                        >
                                            {tab.label} ({tabCounts[tab.id]})
                                        </Button>
                                    );
                                })}
                            </HStack>
                            <Button
                                size="sm"
                                variant="outline"
                                leftIcon={<Icon as={TbDownload as React.ElementType} />}
                                onClick={handleDownload}
                                isLoading={isDownloading}
                                isDisabled={!jobId || totalOutcomeRows === 0}
                            >
                                Download
                            </Button>
                        </Flex>

                        {previewQuery.isLoading ? (
                            <Flex justify="center" py={12}>
                                <Spinner />
                            </Flex>
                        ) : previewQuery.isError ? (
                            <Text color="red.500" fontSize="sm">
                                Failed to load import rows.
                            </Text>
                        ) : rows.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" py={8} textAlign="center">
                                {EMPTY_COPY[rowFilter]}
                            </Text>
                        ) : (
                            <Box>
                                <Box
                                    overflowX="auto"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                >
                                    <Table size="sm">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                {showIssues ? (
                                                    <Th
                                                        w="1%"
                                                        px={2}
                                                        whiteSpace="nowrap"
                                                        textAlign="center"
                                                    >
                                                        Issue
                                                    </Th>
                                                ) : null}
                                                <Th whiteSpace="nowrap">Name</Th>
                                                <Th whiteSpace="nowrap">Mfg. Part Number</Th>
                                                <Th whiteSpace="nowrap">Additional Part Numbers</Th>
                                                <Th whiteSpace="nowrap">Category</Th>
                                                <Th whiteSpace="nowrap">Part Type</Th>
                                                <Th whiteSpace="nowrap">Supplier</Th>
                                                <Th isNumeric whiteSpace="nowrap">
                                                    In-Stock
                                                </Th>
                                                <Th isNumeric whiteSpace="nowrap">
                                                    Avg Unit Cost
                                                </Th>
                                                <Th whiteSpace="nowrap">Location</Th>
                                                <Th whiteSpace="nowrap">Unit</Th>
                                                <Th isNumeric whiteSpace="nowrap">
                                                    Reorder
                                                </Th>
                                                <Th isNumeric whiteSpace="nowrap">
                                                    Target
                                                </Th>
                                                <Th whiteSpace="nowrap">Bins</Th>
                                                <Th whiteSpace="nowrap">Tags</Th>
                                                <Th whiteSpace="nowrap">Image</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {rows.map((row) => {
                                                const rowIssue = showIssues
                                                    ? issueColumnTooltip(row)
                                                    : null;
                                                const imageIssue = showIssues
                                                    ? issueMessageForField(row, 'imageUrl')
                                                    : null;
                                                return (
                                                    <Tr key={row.rowId}>
                                                        {showIssues ? (
                                                            <Td
                                                                w="1%"
                                                                px={2}
                                                                textAlign="center"
                                                                verticalAlign="middle"
                                                            >
                                                                <Flex justify="center">
                                                                    <FieldIssueIcon
                                                                        message={rowIssue}
                                                                        ml={0}
                                                                    />
                                                                </Flex>
                                                            </Td>
                                                        ) : null}
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="name"
                                                            row={row}
                                                        >
                                                            {row.name || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="manufacturerPartNumber"
                                                            row={row}
                                                        >
                                                            {row.manufacturerPartNumber || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="additionalPartNumbers"
                                                            row={row}
                                                        >
                                                            {(row.additionalPartNumbers ?? []).join(
                                                                ', ',
                                                            ) || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="category"
                                                            row={row}
                                                        >
                                                            {categoryLabel(row.category)}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="partType"
                                                            row={row}
                                                        >
                                                            {row.partType
                                                                ? formatInventoryPartTypeLabel(row.partType)
                                                                : '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="supplierName"
                                                            row={row}
                                                        >
                                                            {row.supplierName || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="initialInStockQuantity"
                                                            row={row}
                                                            isNumeric
                                                        >
                                                            {row.initialInStockQuantity ?? '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="unitCost"
                                                            row={row}
                                                            isNumeric
                                                        >
                                                            {row.unitCost != null
                                                                ? formatPrice(row.unitCost)
                                                                : '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="locationTitle"
                                                            row={row}
                                                        >
                                                            {row.locationTitle || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="unit"
                                                            row={row}
                                                        >
                                                            {row.unit || '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="reorderQuantity"
                                                            row={row}
                                                            isNumeric
                                                        >
                                                            {row.reorderQuantity ?? '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="targetQuantity"
                                                            row={row}
                                                            isNumeric
                                                        >
                                                            {row.targetQuantity ?? '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="binNames"
                                                            row={row}
                                                        >
                                                            {(row.binNames ?? []).length > 0
                                                                ? (row.binNames ?? []).join(', ')
                                                                : '—'}
                                                        </ValueWithIssue>
                                                        <ValueWithIssue
                                                            showIssues={showIssues}
                                                            field="tagNames"
                                                            row={row}
                                                        >
                                                            {(row.tagNames ?? []).length > 0
                                                                ? (row.tagNames ?? []).join(', ')
                                                                : '—'}
                                                        </ValueWithIssue>
                                                        <Td>
                                                            <HStack
                                                                align="center"
                                                                spacing={1}
                                                            >
                                                                {row.imageUrl ? (
                                                                    <IconButton
                                                                        aria-label="View image"
                                                                        icon={
                                                                            <Icon
                                                                                as={
                                                                                    TbEye as React.ElementType
                                                                                }
                                                                            />
                                                                        }
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() =>
                                                                            openImage(
                                                                                row.imageUrl!,
                                                                                row.name,
                                                                                row.manufacturerPartNumber,
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Text color="gray.400">—</Text>
                                                                )}
                                                                <FieldIssueIcon
                                                                    message={imageIssue}
                                                                />
                                                            </HStack>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                                {totalPages > 1 ? (
                                    <HStack justify="center" mt={4}>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={setPage}
                                            hasNextPage={page < totalPages}
                                        />
                                    </HStack>
                                ) : null}
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={imageDisclosure.isOpen && !!imageView} onClose={closeImage} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        {imageView
                            ? `${imageView.name.trim() || '—'} · ${imageView.mfg.trim() || '—'}`
                            : 'Image'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {imageView ? (
                            <Image
                                src={imageView.url}
                                alt={imageView.name || 'Inventory item'}
                                maxH="70vh"
                                mx="auto"
                                objectFit="contain"
                            />
                        ) : null}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
